// Live API layer for the Procurement > ProcurementValidity module.
//
// Implements api_contract/procurement/api-contract-procurement-validity.md — the
// ProcurementValidity controller (route base `v1/ProcurementValidity`) on the
// CRUDE host (`06.WebApi.Crude` -> crudeApi), the second stage after Planning is
// Submitted. Same host/auth/error conventions as planningApi.js (which see for
// the crudeApi unwrap + string-reject contract and the blob-download pattern).
//
// Contract-specific notes baked in below:
//  - `id` in the List (section 1) is ProcurementValidity.Id, used as
//    {procurementValidityId} in every route — NOT Procurement.Id.
//  - Two document lists: `planningDocuments[]` (read-only echo from Planning) and
//    `documents[]` (this stage's own, editable). Only the latter is mutable here.
//  - There is NO dedicated master-document lookup endpoint in this contract, so
//    the "Add from Master" picker reads the master CrudeDocumentUpload list
//    directly (DocumentType=Procurement); the server still dedupes on FromMaster.
//  - Download DMUT (section 10) is a server-generated Excel from the SAVED
//    PlanningDmutList (3 columns), not a live eRS call — plain blob GET.
import { crudeApi, crudeAxios } from '../../../../helpers/crude_api_helper';
import {
  STEP_STATUS,
  STEP_STATUS_API,
  DOC_SOURCE,
  DOC_SOURCE_API,
} from './procurementConstants';

const BASE = '/v1/ProcurementValidity';
// The master table the FromMaster ids come from (see note above).
const MASTER_LIST = '/v1/CrudeDocumentUpload/GetListCrudeDocumentUpload';

// ---------------------------------------------------------------------------
// Enum mapping (contract "Enum yang Dipakai" table)
// ---------------------------------------------------------------------------

export const stepStatusToApi = uiStatus =>
  uiStatus === STEP_STATUS.COMPLETED
    ? STEP_STATUS_API.COMPLETED
    : STEP_STATUS_API.IN_PROGRESS;

export const stepStatusFromApi = apiStatus =>
  apiStatus === STEP_STATUS_API.COMPLETED
    ? STEP_STATUS.COMPLETED
    : STEP_STATUS.IN_PROGRESS;

// ---------------------------------------------------------------------------
// Normalizers: API response shape -> the field names the existing tabs render.
// Server ids needed for later mutations are preserved (document.id is the
// mutation key for Delete / ShowToDmut / Download).
// ---------------------------------------------------------------------------

const normalizeCrude = cr => ({
  id: cr.id,
  name: cr.crudeName,
  maxCapacity: cr.maxCapacity ?? null,
  maxCapacityRemark: cr.maxCapacityNote ?? '',
  viaCibOnly: !!cr.isViaCIBOnly,
  // The Procurement Requirement view echoes a single "% Bleeding Crude" column.
  bleedingPct: cr.blendingCrude1 ?? null,
  blendingCrude1: cr.blendingCrude1 ?? null,
  blendingCrude2: cr.blendingCrude2 ?? null,
});

const normalizeCargo = c => ({
  id: c.id,
  cargoType: c.cargoCategoryName,
  cargoCategoryId: c.cargoCategoryId,
  ot: c.otPercentage ?? null,
  otOption: c.operationalToleranceOption || '',
  volume: c.volumeKbbl ?? null,
  ddrFrom: c.ddrFrom || null,
  ddrTo: c.ddrTo || null,
  ddrNotes: c.ddrNotes || '',
  incoterms: (c.incoterms || []).map(i => i.incotermName),
  incotermsNotes: c.incotermsNotes || '',
  plant: c.plantName,
  unit: c.cduName,
  crudes: (c.crudes || []).map(normalizeCrude),
});

const normalizeClause = c => ({
  id: c.id,
  clauseId: c.id,
  title: c.title,
  mandatory: !!c.isMandatoryForDmut,
  summernote: c.note || '',
});

const normalizeDocument = d => ({
  id: d.id, // ProcurementValidityDocument.Id / PlanningDocument.Id
  name: d.documentName,
  source:
    d.source === DOC_SOURCE_API.MASTER ? DOC_SOURCE.MASTER : DOC_SOURCE.CUSTOM,
  sourceApi: d.source,
  showToDmut: !!d.showToDmut,
  fileName: d.fileName,
  fileContentType: d.fileContentType,
  fileSize: d.fileSize,
  storageFileId: d.storageFileId,
  // documents[].modified is the last edit; fall back to created.
  lastUpdated: d.modified || d.created || null,
});

// dmutInvitations[] is nested-per-company (pics[] inside). The Procurement DMUT
// view is a flat one-row-per-PIC table (matching the Download Excel, section 10),
// so flatten here. A company with no PIC still yields one row (blank PIC).
const flattenDmut = invitations =>
  (invitations || []).flatMap(x => {
    const pics =
      x.pics && x.pics.length ? x.pics : [{ picName: '', picEmail: '' }];
    return pics.map((p, idx) => ({
      id: `${x.id}-${p.id || idx}`,
      name: x.dmutName,
      dmutCode: x.dmutCode,
      dmutCargoCategory: x.dmutCargoCategory,
      dmutCountry: x.dmutCountry,
      picName: p.picName || '',
      picEmail: p.picEmail || '',
      invited: true,
    }));
  });

// Full Detail (GetProcurementValidityResponse) -> { record, detail } for the form.
export const normalizeDetail = api => {
  const record = {
    id: api.id, // ProcurementValidity.Id — the {procurementValidityId}
    procurementId: api.procurementId,
    // Needed to download read-only planningDocuments through Planning's own
    // endpoint — they have no download route on ProcurementValidityController.
    planningId: api.planningId,
    procId: api.procurementCode,
    title: api.title,
    procurementType: api.jenisPengadaanName,
    procurementMethod: api.mekanismePengadaanName,
    description: api.description,
    status: api.phaseStatusName, // 'Draft' | 'Submitted'
    createdDate: api.created,
  };

  const detail = {
    requirement: {
      contractType: api.contractType || '',
      startPeriod: api.contractStartPeriod || null,
      endPeriod: api.contractEndPeriod || null,
      cargos: (api.cargos || []).map(normalizeCargo),
      cargoNotes: api.cargoNotes || '',
      clauses: (api.clauses || []).map(normalizeClause),
      additionalNotes: api.otherTermsNotes || '',
    },
    // Read-only Planning echo -> "List Data Document Planning".
    supportingDocuments: (api.planningDocuments || []).map(normalizeDocument),
    // This stage's own editable list -> "List Data Document Requirement".
    requirementDocuments: (api.documents || []).map(normalizeDocument),
    dmut: {
      confirmed: (api.dmutInvitations || []).length > 0,
      rows: flattenDmut(api.dmutInvitations),
    },
    validity: {
      submissionDeadline: api.submissionDeadline || null,
      validityPeriod: api.validityPeriod || null,
    },
    steps: {
      requirement: stepStatusFromApi(api.mainRequirementStepStatus),
      supportingDocument: stepStatusFromApi(api.documentStepStatus),
      dmut: stepStatusFromApi(api.dmutInvitationStepStatus),
      validity: stepStatusFromApi(api.validityStepStatus),
    },
  };

  return { record, detail };
};

// List item (GetProcurementValidityListResponse) -> the fields ProcurementView's
// columns read. The four step columns are rendered off `detail.steps`, so build
// a minimal `detail` here too. `id` is ProcurementValidity.Id.
export const normalizeListItem = r => ({
  id: r.id,
  procurementId: r.procurementId,
  procId: r.procurementCode,
  title: r.title,
  procurementType: r.jenisPengadaanName,
  procurementMethod: r.mekanismePengadaanName,
  description: r.description,
  contractType: r.contractType || '', // 'SPOT' | 'TERM' (dari Planning)
  submissionDeadline: r.submissionDeadline || null,
  validityPeriod: r.validityPeriod || null,
  mainStatus: r.phaseName, // 'Procurement' | 'Tender Opening'
  status: r.phaseStatusName, // 'Draft' | 'Submitted'
  createdDate: r.created,
  updatedDate: r.modified,
  detail: {
    steps: {
      requirement: stepStatusFromApi(r.mainRequirementStepStatus),
      supportingDocument: stepStatusFromApi(r.documentStepStatus),
      dmut: stepStatusFromApi(r.dmutInvitationStepStatus),
      validity: stepStatusFromApi(r.validityStepStatus),
    },
  },
});

// ===========================================================================
// Section 1 / 2 — List + Detail
// ===========================================================================

// 1 — GET /v1/ProcurementValidity (paginated). `sortField` is PascalCase +
// whitelisted (section 1); `sortOrder` only applies when a field is set.
export const listProcurement = ({
  page = 1,
  pageSize = 10,
  searchText = '',
  sortField = null,
  sortOrder = null,
} = {}) =>
  crudeApi
    .get(BASE, {
      page,
      pageSize,
      searchText: searchText || undefined,
      sortField: sortField || undefined,
      sortOrder: sortField ? sortOrder || undefined : undefined,
    })
    .then(res => ({
      items: (res?.items || []).map(normalizeListItem),
      totalCount: res?.totalCount ?? 0,
    }));

// 2 — GET /v1/ProcurementValidity/{id}. Returns normalized { record, detail }.
export const getProcurement = procurementValidityId =>
  crudeApi.get(`${BASE}/${procurementValidityId}`).then(normalizeDetail);

// ===========================================================================
// Section 3–8 — Supporting Documents (this stage's own `documents[]`)
// ===========================================================================

// Master picker source. No scoped lookup exists in the contract, so read the
// master CrudeDocumentUpload list (DocumentType=Procurement). Returns raw items
// [{ id, documentName, ... }]; the caller filters out already-added by name.
export const lookupMasterDocuments = (searchText = '') =>
  crudeApi
    .get(MASTER_LIST, {
      page: 1,
      pageSize: 50,
      searchText: searchText || undefined,
      documentType: 'Procurement',
    })
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// 3 — POST /v1/ProcurementValidity/{id}/Document/FromMaster. Returns { documentIds }.
export const addDocumentsFromMaster = (procurementValidityId, documents) =>
  crudeApi.create(`${BASE}/${procurementValidityId}/Document/FromMaster`, {
    documents: (documents || []).map(d => ({
      crudeDocumentUploadId: d.crudeDocumentUploadId,
      showToDmut: d.showToDmut !== false,
    })),
  });

// 4 — POST /v1/ProcurementValidity/{id}/Document/Upload (multipart). Field
// casing matches the [FromForm] binder: DocumentName / ShowToDmut / File.
export const uploadDocument = (
  procurementValidityId,
  { documentName, showToDmut, file }
) =>
  crudeApi.postMultipart(`${BASE}/${procurementValidityId}/Document/Upload`, {
    DocumentName: documentName,
    ShowToDmut: showToDmut ? 'true' : 'false',
    File: file,
  });

// 5 — DELETE /v1/ProcurementValidity/{id}/Document/{documentId} (204).
export const deleteDocument = (procurementValidityId, documentId) =>
  crudeApi.delete(`${BASE}/${procurementValidityId}/Document/${documentId}`);

// 6 — PUT /v1/ProcurementValidity/{id}/Document/{documentId}/ShowToDmut (204).
export const setDocumentShowToDmut = (
  procurementValidityId,
  documentId,
  showToDmut
) =>
  crudeApi.put(
    `${BASE}/${procurementValidityId}/Document/${documentId}/ShowToDmut`,
    { showToDmut: !!showToDmut }
  );

// 7 — GET /v1/ProcurementValidity/{id}/Document/{documentId}/Download (blob).
// Only for THIS stage's own `documents[]`.
export const downloadDocument = (procurementValidityId, documentId) =>
  crudeAxios.get(
    `${BASE}/${procurementValidityId}/Document/${documentId}/Download`,
    { responseType: 'blob' }
  );

// Read-only `planningDocuments[]` have NO download endpoint on
// ProcurementValidityController (contract 2026-07-23) — download them through
// Planning's own route using the `planningId` from the Detail response
// (api-contract-planning.md section 18).
export const downloadPlanningDocument = (planningId, documentId) =>
  crudeAxios.get(`/v1/Planning/${planningId}/Document/${documentId}/Download`, {
    responseType: 'blob',
  });

// 8 — PUT /v1/ProcurementValidity/{id}/Document/Status (204). Tab-level
// Save / Save & Completed for Supporting Document. Completed requires >=1
// document in `documents[]` (server-enforced; the tab checks first).
export const saveDocumentStatus = (procurementValidityId, stepStatus) =>
  crudeApi.put(`${BASE}/${procurementValidityId}/Document/Status`, {
    stepStatus: stepStatusToApi(stepStatus),
  });

// ===========================================================================
// Section 9 — Validity
// ===========================================================================

// PUT /v1/ProcurementValidity/{id}/SaveValidity (204). Both dates nullable when
// InProgress; required when Completed (server + FluentValidation). Send ISO
// strings with the correct offset (e.g. +07:00) — the server compares to UtcNow.
export const saveValidity = (
  procurementValidityId,
  { stepStatus, submissionDeadline, validityPeriod }
) =>
  crudeApi.put(`${BASE}/${procurementValidityId}/SaveValidity`, {
    stepStatus: stepStatusToApi(stepStatus),
    submissionDeadline: submissionDeadline || null,
    validityPeriod: validityPeriod || null,
  });

// ===========================================================================
// Section 10 — Download DMUT Excel (from saved PlanningDmutList, 3 columns)
// ===========================================================================

export const downloadDmutInvitation = procurementValidityId =>
  crudeAxios.get(`${BASE}/${procurementValidityId}/DmutInvitation/Download`, {
    responseType: 'blob',
  });

// ===========================================================================
// Section 11 — Submit (locks ProcurementValidity, opens Tender Opening)
// ===========================================================================

// POST /v1/ProcurementValidity/{id}/Submit (no body, 204). Requires all four
// steps Completed (server-enforced); in practice only Supporting Document +
// Validity need manual completion (Requirement/DMUT are auto-Completed echoes).
export const submitProcurement = procurementValidityId =>
  crudeApi.create(`${BASE}/${procurementValidityId}/Submit`, {});
